import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  FiChevronRight, FiChevronDown, FiEdit2, FiCheck, FiX,
  FiEye, FiEyeOff, FiArrowUp, FiArrowDown, FiRotateCcw, FiBookOpen,
} from 'react-icons/fi';
import { loadTable, saveRow, addRow, removeRow, getTableData } from '../../db-viewer/data/dataAccess';
import { addNotification } from '../../../lib/notifications';
import { setFormDirty } from '../../../lib/unsavedChanges';
import './SetupHeadersTab.css';

// =============================================
// Types
// =============================================
type RefType = 'header' | 'category' | 'sub_category';

interface HeaderRow {
  header_id: number;
  header_name: string;
  header_code: string;
  sort_order: number;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

interface CategoryRow {
  cate_id: number;
  header_id: number;
  cate_name: string;
  cate_code: string;
  sort_order: number;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

interface SubCategoryRow {
  sub_cate_id: number;
  cate_id: number;
  sub_cate_name: string;
  sub_cate_code: string;
  sort_order: number;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

interface CustomRow {
  custom_id: number;
  account_owner_id: number;
  ref_type: RefType;
  ref_id: number;
  custom_name: string | null;
  custom_sort_order: number | null;
  is_hidden: number;
  is_active: number;
  is_deleted: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

// =============================================
// Helpers
// =============================================
const NOW_ISO = () => new Date().toISOString().replace('T', ' ').substring(0, 19);
const CUSTOM_TABLE = 'acctg_finsta_user_custom';
const CUSTOM_PK = 'custom_id';

const nodeKey = (refType: RefType, refId: number) => `${refType}-${refId}`;

function getCurrentUser(usersData: Record<string, any>[]) {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const session = JSON.parse(raw);
    const sessionId = Number(session.id || session.user_id);
    if (!sessionId) return null;
    const userRow = usersData.find((u: any) => Number(u.user_id) === sessionId);
    if (!userRow) return null;
    return {
      user_id: sessionId,
      owner_id: userRow.owner_id == null || userRow.owner_id === '' ? null : Number(userRow.owner_id),
      first_name: userRow.first_name || '',
      last_name: userRow.last_name || '',
    };
  } catch { /* ignore */ }
  return null;
}

function getAccountOwnerForUser(userId: number, usersData: Record<string, any>[]): Record<string, any> | null {
  // eslint-disable-next-line eqeqeq
  const user = usersData.find((u: any) => u.user_id == userId);
  if (!user) return null;
  if (user.owner_id == null || user.owner_id === '') return user;
  // eslint-disable-next-line eqeqeq
  return usersData.find((u: any) => u.user_id == user.owner_id) || null;
}

// =============================================
// Component
// =============================================
const SetupHeadersTab: React.FC = () => {
  const [usersData, setUsersData] = useState<Record<string, any>[]>([]);
  const [headers, setHeaders] = useState<HeaderRow[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategoryRow[]>([]);
  const [customs, setCustoms] = useState<CustomRow[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<{ key: string; value: string } | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const loadCustoms = useCallback(async () => {
    try {
      const rows = await loadTable(CUSTOM_TABLE);
      setCustoms(rows as CustomRow[]);
    } catch {
      setCustoms(getTableData(CUSTOM_TABLE) as CustomRow[]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        const [usersRows, hdrRows, cateRows, subCateRows, customRows] = await Promise.all([
          loadTable('users'),
          loadTable('acctg_finsta_headers'),
          loadTable('acctg_finsta_cate'),
          loadTable('acctg_finsta_cate_sub'),
          loadTable(CUSTOM_TABLE),
        ]);
        if (cancelled) return;
        setUsersData(usersRows);
        setHeaders(hdrRows as HeaderRow[]);
        setCategories(cateRows as CategoryRow[]);
        setSubCategories(subCateRows as SubCategoryRow[]);
        setCustoms(customRows as CustomRow[]);
        setDataLoaded(true);
      } catch {
        if (cancelled) return;
        setUsersData(getTableData('users'));
        setHeaders(getTableData('acctg_finsta_headers') as HeaderRow[]);
        setCategories(getTableData('acctg_finsta_cate') as CategoryRow[]);
        setSubCategories(getTableData('acctg_finsta_cate_sub') as SubCategoryRow[]);
        setCustoms(getTableData(CUSTOM_TABLE) as CustomRow[]);
        setDataLoaded(true);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, []);

  const currentUser = useMemo(() => dataLoaded ? getCurrentUser(usersData) : null, [usersData, dataLoaded]);
  const accountOwner = useMemo(() => {
    if (!currentUser) return null;
    return getAccountOwnerForUser(currentUser.user_id, usersData);
  }, [currentUser, usersData]);
  const accountOwnerId = accountOwner ? Number(accountOwner.user_id) : 0;
  const isAccountOwner = currentUser?.owner_id == null;

  // ---- Override lookup ----
  const customFor = useCallback((refType: RefType, refId: number): CustomRow | undefined => {
    return customs.find(c =>
      c.ref_type === refType &&
      // eslint-disable-next-line eqeqeq
      c.ref_id == refId &&
      // eslint-disable-next-line eqeqeq
      c.account_owner_id == accountOwnerId &&
      // eslint-disable-next-line eqeqeq
      c.is_deleted != 1
    );
  }, [customs, accountOwnerId]);

  const effName = useCallback((refType: RefType, refId: number, systemName: string): string => {
    const c = customFor(refType, refId);
    return c && c.custom_name != null && c.custom_name !== '' ? c.custom_name : systemName;
  }, [customFor]);

  const isHidden = useCallback((refType: RefType, refId: number): boolean => {
    const c = customFor(refType, refId);
    // eslint-disable-next-line eqeqeq
    return !!c && c.is_hidden == 1;
  }, [customFor]);

  const effSort = useCallback((refType: RefType, refId: number, systemSort: number): number => {
    const c = customFor(refType, refId);
    return c && c.custom_sort_order != null ? Number(c.custom_sort_order) : Number(systemSort);
  }, [customFor]);

  const isCustomized = useCallback((refType: RefType, refId: number): boolean => {
    const c = customFor(refType, refId);
    if (!c) return false;
    return (c.custom_name != null && c.custom_name !== '') || c.custom_sort_order != null || c.is_hidden == 1;
  }, [customFor]);

  // ---- Build ordered tree ----
  const sortedHeaders = useMemo(() => {
    return headers
      .filter(h => h.is_deleted !== 1)
      .filter(h => showHidden || !isHidden('header', h.header_id))
      .slice()
      .sort((a, b) => effSort('header', a.header_id, a.sort_order) - effSort('header', b.header_id, b.sort_order));
  }, [headers, showHidden, isHidden, effSort]);

  const categoriesForHeader = useCallback((headerId: number) => {
    return categories
      .filter(c => c.is_deleted !== 1)
      // eslint-disable-next-line eqeqeq
      .filter(c => c.header_id == headerId)
      .filter(c => showHidden || !isHidden('category', c.cate_id))
      .slice()
      .sort((a, b) => effSort('category', a.cate_id, a.sort_order) - effSort('category', b.cate_id, b.sort_order));
  }, [categories, showHidden, isHidden, effSort]);

  const subCategoriesForCategory = useCallback((cateId: number) => {
    return subCategories
      .filter(s => s.is_deleted !== 1)
      // eslint-disable-next-line eqeqeq
      .filter(s => s.cate_id == cateId)
      .filter(s => showHidden || !isHidden('sub_category', s.sub_cate_id))
      .slice()
      .sort((a, b) => effSort('sub_category', a.sub_cate_id, a.sort_order) - effSort('sub_category', b.sub_cate_id, b.sort_order));
  }, [subCategories, showHidden, isHidden, effSort]);

  // ---- Persistence ----
  const flash = useCallback((msg: string) => {
    setSavedMsg(msg);
    setTimeout(() => setSavedMsg(''), 2500);
  }, []);

  const notifyOwner = useCallback((summary: string) => {
    if (!currentUser || isAccountOwner || !accountOwnerId) return;
    const userName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'A user';
    addNotification({
      account_owner_id: accountOwnerId,
      type: 'finsta_custom',
      title: 'Financial statement layout changed',
      message: `${userName} ${summary}`,
      actor_user_id: currentUser.user_id,
      actor_name: userName,
    });
  }, [currentUser, isAccountOwner, accountOwnerId]);

  // Write a single override without reloading (caller reloads once after a batch).
  const writeCustom = useCallback(async (refType: RefType, refId: number, updates: Partial<CustomRow>) => {
    const existing = customFor(refType, refId);
    if (existing) {
      await saveRow(CUSTOM_TABLE, existing.custom_id, { ...updates, updated_at: NOW_ISO() }, CUSTOM_PK);
    } else {
      await addRow(CUSTOM_TABLE, {
        account_owner_id: accountOwnerId,
        ref_type: refType,
        ref_id: refId,
        custom_name: null,
        custom_sort_order: null,
        is_hidden: 0,
        is_active: 1,
        is_deleted: 0,
        created_by: currentUser?.user_id ?? accountOwnerId,
        created_at: NOW_ISO(),
        updated_at: NOW_ISO(),
        ...updates,
      }, CUSTOM_PK);
    }
  }, [customFor, accountOwnerId, currentUser]);

  // ---- Actions ----
  const startRename = useCallback((refType: RefType, refId: number, currentName: string) => {
    setEditing({ key: nodeKey(refType, refId), value: currentName });
    setFormDirty(true);
  }, []);

  const cancelRename = useCallback(() => {
    setEditing(null);
    setFormDirty(false);
  }, []);

  const commitRename = useCallback(async (refType: RefType, refId: number, systemName: string) => {
    if (!editing) return;
    const trimmed = editing.value.trim();
    setEditing(null);
    setFormDirty(false);
    // No change (matches system name or unchanged) → nothing to persist
    const current = effName(refType, refId, systemName);
    if (trimmed === current) return;
    setBusy(true);
    try {
      // Renaming back to the exact system name clears the override name.
      const newName = trimmed === '' || trimmed === systemName ? null : trimmed;
      await writeCustom(refType, refId, { custom_name: newName });
      await loadCustoms();
      notifyOwner(newName ? `renamed "${systemName}" to "${newName}".` : `reset the name of "${systemName}".`);
      flash('Saved.');
    } finally {
      setBusy(false);
    }
  }, [editing, effName, writeCustom, loadCustoms, notifyOwner, flash]);

  const toggleHidden = useCallback(async (refType: RefType, refId: number, name: string) => {
    const next = isHidden(refType, refId) ? 0 : 1;
    setBusy(true);
    try {
      await writeCustom(refType, refId, { is_hidden: next });
      await loadCustoms();
      notifyOwner(`${next ? 'hid' : 'unhid'} "${name}".`);
      flash('Saved.');
    } finally {
      setBusy(false);
    }
  }, [isHidden, writeCustom, loadCustoms, notifyOwner, flash]);

  // Reorder a node within its sibling list by reindexing all siblings.
  const reorder = useCallback(async (
    refType: RefType,
    siblings: { id: number; sort: number }[],
    index: number,
    direction: -1 | 1,
  ) => {
    const target = index + direction;
    if (target < 0 || target >= siblings.length) return;
    const reordered = siblings.slice();
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    setBusy(true);
    try {
      for (let i = 0; i < reordered.length; i++) {
        await writeCustom(refType, reordered[i].id, { custom_sort_order: (i + 1) * 10 });
      }
      await loadCustoms();
      notifyOwner('reordered the financial statement layout.');
      flash('Order saved.');
    } finally {
      setBusy(false);
    }
  }, [writeCustom, loadCustoms, notifyOwner, flash]);

  const resetNode = useCallback(async (refType: RefType, refId: number, name: string) => {
    const existing = customFor(refType, refId);
    if (!existing) return;
    if (!window.confirm(`Reset "${name}" to its default name, visibility, and order?`)) return;
    setBusy(true);
    try {
      await removeRow(CUSTOM_TABLE, existing.custom_id, CUSTOM_PK);
      await loadCustoms();
      notifyOwner(`reset "${name}" to default.`);
      flash('Reset to default.');
    } finally {
      setBusy(false);
    }
  }, [customFor, loadCustoms, notifyOwner, flash]);

  // ---- Expand / collapse ----
  const toggleExpand = useCallback((key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    const keys = new Set<string>();
    headers.filter(h => h.is_deleted !== 1).forEach(h => {
      keys.add(nodeKey('header', h.header_id));
      categories.filter(c => c.is_deleted !== 1 && c.header_id == h.header_id).forEach(c => {
        keys.add(nodeKey('category', c.cate_id));
      });
    });
    setExpanded(keys);
  }, [headers, categories]);

  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  // =============================================
  // Render
  // =============================================
  if (!dataLoaded) {
    return <div className="shf-tab"><div className="shf-loading">Loading financial statement structure…</div></div>;
  }

  if (!accountOwnerId) {
    return (
      <div className="shf-tab">
        <div className="shf-empty">
          <div className="shf-empty-icon"><FiBookOpen size={48} /></div>
          <p>Could not determine your account. Please sign in again.</p>
        </div>
      </div>
    );
  }

  const renderRowActions = (refType: RefType, refId: number, name: string, siblings: { id: number; sort: number }[], index: number) => {
    const hidden = isHidden(refType, refId);
    const customized = isCustomized(refType, refId);
    return (
      <div className="shf-row-actions">
        <button className="shf-icon-btn" title="Rename" disabled={busy} onClick={(e) => { e.stopPropagation(); startRename(refType, refId, name); }}>
          <FiEdit2 size={14} />
        </button>
        <button className="shf-icon-btn" title="Move up" disabled={busy || index === 0} onClick={(e) => { e.stopPropagation(); reorder(refType, siblings, index, -1); }}>
          <FiArrowUp size={14} />
        </button>
        <button className="shf-icon-btn" title="Move down" disabled={busy || index === siblings.length - 1} onClick={(e) => { e.stopPropagation(); reorder(refType, siblings, index, 1); }}>
          <FiArrowDown size={14} />
        </button>
        <button className={`shf-icon-btn ${hidden ? 'shf-icon-btn-warn' : ''}`} title={hidden ? 'Show' : 'Hide'} disabled={busy} onClick={(e) => { e.stopPropagation(); toggleHidden(refType, refId, name); }}>
          {hidden ? <FiEyeOff size={14} /> : <FiEye size={14} />}
        </button>
        <button className="shf-icon-btn" title="Reset to default" disabled={busy || !customized} onClick={(e) => { e.stopPropagation(); resetNode(refType, refId, name); }}>
          <FiRotateCcw size={14} />
        </button>
      </div>
    );
  };

  const renderName = (refType: RefType, refId: number, systemName: string) => {
    const key = nodeKey(refType, refId);
    if (editing && editing.key === key) {
      return (
        <span className="shf-rename-wrap" onClick={(e) => e.stopPropagation()}>
          <input
            className="shf-rename-input"
            autoFocus
            value={editing.value}
            placeholder={systemName}
            onChange={(e) => setEditing({ key, value: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename(refType, refId, systemName);
              if (e.key === 'Escape') cancelRename();
            }}
          />
          <button className="shf-icon-btn shf-icon-btn-ok" title="Save" onClick={() => commitRename(refType, refId, systemName)}><FiCheck size={14} /></button>
          <button className="shf-icon-btn" title="Cancel" onClick={cancelRename}><FiX size={14} /></button>
        </span>
      );
    }
    const renamed = effName(refType, refId, systemName) !== systemName;
    return (
      <span className="shf-node-name">
        {effName(refType, refId, systemName)}
        {renamed && <span className="shf-orig-name" title="Default name">({systemName})</span>}
      </span>
    );
  };

  return (
    <div className="shf-tab">
      <div className="shf-info-banner">
        <FiBookOpen size={16} />
        <span>
          Customize how the standard financial-statement structure appears across your account — rename, reorder, or hide
          headers, categories, and detail types. The platform defaults stay intact; these changes apply only to your account.
        </span>
      </div>

      <div className="shf-toolbar">
        <div className="shf-toolbar-left">
          <button className="shf-text-btn" onClick={expandAll}>Expand all</button>
          <span className="shf-divider">·</span>
          <button className="shf-text-btn" onClick={collapseAll}>Collapse all</button>
        </div>
        <div className="shf-toolbar-right">
          <label className="shf-show-hidden">
            <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
            Show hidden
          </label>
          {savedMsg && <span className="shf-saved-msg"><FiCheck size={14} /> {savedMsg}</span>}
        </div>
      </div>

      <div className="shf-tree">
        {sortedHeaders.map((h, hi) => {
          const hKey = nodeKey('header', h.header_id);
          const hOpen = expanded.has(hKey);
          const cats = categoriesForHeader(h.header_id);
          const headerSiblings = sortedHeaders.map(x => ({ id: x.header_id, sort: x.sort_order }));
          const hHidden = isHidden('header', h.header_id);
          return (
            <div key={hKey} className="shf-header-block">
              <div className={`shf-node shf-node-header ${hHidden ? 'shf-node-hidden' : ''}`} onClick={() => toggleExpand(hKey)}>
                <button className="shf-caret" onClick={(e) => { e.stopPropagation(); toggleExpand(hKey); }}>
                  {cats.length > 0 ? (hOpen ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />) : <span className="shf-caret-spacer" />}
                </button>
                <span className="shf-node-code">{h.header_code}</span>
                {renderName('header', h.header_id, h.header_name)}
                {hHidden && <span className="shf-hidden-tag">Hidden</span>}
                {renderRowActions('header', h.header_id, effName('header', h.header_id, h.header_name), headerSiblings, hi)}
              </div>

              {hOpen && cats.map((c, ci) => {
                const cKey = nodeKey('category', c.cate_id);
                const cOpen = expanded.has(cKey);
                const subs = subCategoriesForCategory(c.cate_id);
                const catSiblings = cats.map(x => ({ id: x.cate_id, sort: x.sort_order }));
                const cHidden = isHidden('category', c.cate_id);
                return (
                  <div key={cKey} className="shf-cate-block">
                    <div className={`shf-node shf-node-cate ${cHidden ? 'shf-node-hidden' : ''}`} onClick={() => toggleExpand(cKey)}>
                      <button className="shf-caret" onClick={(e) => { e.stopPropagation(); toggleExpand(cKey); }}>
                        {subs.length > 0 ? (cOpen ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />) : <span className="shf-caret-spacer" />}
                      </button>
                      {renderName('category', c.cate_id, c.cate_name)}
                      {cHidden && <span className="shf-hidden-tag">Hidden</span>}
                      {renderRowActions('category', c.cate_id, effName('category', c.cate_id, c.cate_name), catSiblings, ci)}
                    </div>

                    {cOpen && subs.map((s, si) => {
                      const sKey = nodeKey('sub_category', s.sub_cate_id);
                      const subSiblings = subs.map(x => ({ id: x.sub_cate_id, sort: x.sort_order }));
                      const sHidden = isHidden('sub_category', s.sub_cate_id);
                      return (
                        <div key={sKey} className={`shf-node shf-node-sub ${sHidden ? 'shf-node-hidden' : ''}`}>
                          <span className="shf-caret-spacer" />
                          {renderName('sub_category', s.sub_cate_id, s.sub_cate_name)}
                          {sHidden && <span className="shf-hidden-tag">Hidden</span>}
                          {renderRowActions('sub_category', s.sub_cate_id, effName('sub_category', s.sub_cate_id, s.sub_cate_name), subSiblings, si)}
                        </div>
                      );
                    })}
                    {cOpen && subs.length === 0 && (
                      <div className="shf-node shf-node-sub shf-node-empty"><span className="shf-caret-spacer" />No detail types.</div>
                    )}
                  </div>
                );
              })}
              {hOpen && cats.length === 0 && (
                <div className="shf-node shf-node-cate shf-node-empty"><span className="shf-caret-spacer" />No categories.</div>
              )}
            </div>
          );
        })}
        {sortedHeaders.length === 0 && (
          <div className="shf-empty">
            <div className="shf-empty-icon"><FiBookOpen size={48} /></div>
            <p>No financial statement headers found.</p>
            {!showHidden && <p className="shf-empty-hint">All headers may be hidden — enable “Show hidden” above.</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default SetupHeadersTab;
